/**
 * Lightweight cache invalidation for Dashboard lesson plans.
 * Persists across Dashboard unmounts so we can skip refetch when returning within stale time.
 * Cache is keyed per session token to avoid cross-user leakage.
 */

const stateByKey = new Map();

const STALE_MS = 60 * 1000; // 60 seconds
const GLOBAL_KEY = '__global__';

function getState(key = GLOBAL_KEY) {
  if (!stateByKey.has(key)) {
    stateByKey.set(key, {
      invalidated: false,
      lastFetchedAt: 0,
      cachedInProgress: [],
      cachedCompleted: [],
    });
  }
  return stateByKey.get(key);
}

/** Call when lesson plans may have changed (create, save, delete). Dashboard will refetch on next mount. */
export function invalidateDashboardPlans(key) {
  if (key) {
    getState(key).invalidated = true;
    return;
  }

  // If no key is passed, invalidate all known caches.
  if (stateByKey.size === 0) {
    getState(GLOBAL_KEY).invalidated = true;
    return;
  }

  for (const state of stateByKey.values()) {
    state.invalidated = true;
  }
}

/** Returns true if cache was invalidated. */
export function isInvalidated(key = GLOBAL_KEY) {
  return getState(key).invalidated;
}

/** Clears the invalidation flag after successful refetch. */
export function clearInvalidation(key = GLOBAL_KEY) {
  getState(key).invalidated = false;
}

export function getLastFetchedAt(key = GLOBAL_KEY) {
  return getState(key).lastFetchedAt;
}

export function setLastFetchedAt(t, key = GLOBAL_KEY) {
  getState(key).lastFetchedAt = t;
}

export function isStale(key = GLOBAL_KEY) {
  const state = getState(key);
  return state.lastFetchedAt === 0 || Date.now() - state.lastFetchedAt > STALE_MS;
}

/** Store plans so we can restore when skipping refetch (state is lost on unmount). */
export function setCachedPlans(inProgress, completed, key = GLOBAL_KEY) {
  const state = getState(key);
  state.cachedInProgress = inProgress || [];
  state.cachedCompleted = completed || [];
}

export function getCachedPlans(key = GLOBAL_KEY) {
  const state = getState(key);
  return { inProgress: state.cachedInProgress, completed: state.cachedCompleted };
}
